import { UnsupportedChainIdError, useWeb3React } from '@web3-react/core';
import { NoEthereumProviderError } from '@web3-react/injected-connector';
import {
    UserRejectedRequestError,
    WalletConnectConnector,
} from '@web3-react/walletconnect-connector';
import React, { ReactNode, useCallback, useEffect, useReducer } from 'react';
import { CHAIN_INFO, SupportedConnectors } from '../../config/constants';
import useAutoConnect from '../../hooks/web3/useAutoConnect';
import { networkConnector } from '../../web3/connectors';
import useModalState from '../modal/useModalState';
import ConnectionContext from './connectionContext';
import connectionReducer from './connectionReducer';
import { config } from '../../config/config';
import useToastState from '../toast/useToastState';
import { hexStripZeros } from 'ethers/lib/utils';
import { BigNumber } from 'ethers';

type Props = {
    children: ReactNode;
};

const ConnectionState: React.FC<Props> = ({ children }) => {
    const { error, activate, chainId, deactivate, setError } = useWeb3React();

    const { closeModal } = useModalState();

    const { setToast } = useToastState();

    const resetWalletConnector = useCallback((connector: any) => {
        if (
            connector &&
            connector instanceof WalletConnectConnector &&
            connector.walletConnectProvider?.connector?.uri
        ) {
            connector.walletConnectProvider = undefined;
        }
    }, []);

    const activateWallet = useCallback(
        async (name: SupportedConnectors, connector: any) => {
            try {
                await activate(connector, undefined, true);
                dispatch({
                    type: 'SET_WALLET_CONNECTION',
                    payload: {
                        name,
                        connector,
                    },
                });
                closeModal('connectWalletModal');
            } catch (error: any) {
                if (error instanceof UnsupportedChainIdError) {
                    const provider = await connector.getProvider();

                    if (provider.request) {
                        try {
                            await provider.request({
                                method: 'wallet_switchEthereumChain',
                                params: [
                                    {
                                        chainId: hexStripZeros(
                                            BigNumber.from(
                                                config.enabledChains[0]
                                            ).toHexString()
                                        ),
                                    },
                                ],
                            });

                            activateWallet(name, connector);

                            return;
                        } catch (error) {
                            return;
                        }
                    }
                }

                setError(error);
                resetWalletConnector(connector);
            }
        },
        [activate, closeModal, setError, resetWalletConnector]
    );

    const disconnectWallet = () => {
        dispatch({
            type: 'DISCONNECT_WALLET',
        });
        deactivate();
        closeModal('connectWalletModal');
    };

    const handleDisconnect = async () => {
        disconnectWallet();

        if (activeConnector.name === SupportedConnectors.METAMASK) return;
        activeConnector.connector.close();
    };

    const initialState = {
        activeConnector: {
            connector: null,
            name: null,
        },
        activateWallet,
        disconnectWallet,
        handleDisconnect,
    };

    const [state, dispatch] = useReducer(connectionReducer, initialState);

    const { activeConnector } = state;

    useEffect(() => {
        if (chainId && !config.enabledChains.includes(chainId)) {
            setError(new UnsupportedChainIdError(chainId, config.enabledChains));
        }
    }, [chainId, setError]);

    useEffect(() => {
        if (!error) return;

        let message = '';

        switch (true) {
            case error instanceof UnsupportedChainIdError:
                message = `Please connect to ${config.enabledChains
                    .map((chain) => CHAIN_INFO[chain]?.name)
                    .join(', ')}`;

                break;
            case error instanceof NoEthereumProviderError:
                message = 'No ethereum provider found';
                break;
            case error.name === 'TransportError':
                message = 'U2F browser support is required';
                break;
            case error.message === 'Transport is missing':
                message = 'Trezor Bridge not installed';
                break;
            case error instanceof UserRejectedRequestError:
            case error.message === 'User cancelled login':
                return;
            default:
                console.log(error);
        }

        if (message) {
            setToast({ type: 'primary', message }, message);
        }

        closeModal('connectWalletModal');
    }, [error, setToast, closeModal]);

    return (
        <ConnectionContext.Provider
            value={{
                activeConnector,
                activateWallet,
                disconnectWallet,
                handleDisconnect,
            }}
        >
            <AttempInitializeConnection />
            {children}
        </ConnectionContext.Provider>
    );
};

export default ConnectionState;

const AttempInitializeConnection = () => {
    const { active, activate } = useWeb3React();

    const tried = useAutoConnect();

    useEffect(() => {
        if (tried && !active) {
            activate(networkConnector);
        }
    }, [tried, active, activate]);

    return null;
};
